import { appLegalConfig } from './appLegalConfig'

/** Web + mobile UI languages for legal copy. */
export type LegalLocale = 'en' | 'lt'

export type LegalDocId =
  | 'privacy'
  | 'terms'
  | 'buyer_protection'
  | 'eula'
  | 'acceptable_use'
  | 'support'
  | 'licenses'
  | 'account_data'

type Doc = { title: string; body: string }

function fill(body: string): string {
  const c = appLegalConfig
  const siteBase = c.websiteUrl.replace(/\/$/, '')
  // Public SPA routes use locale prefix (see pathWithLocale); /legal/* alone may boot before redirect.
  const termsUrl = (c.termsWebUrl || `${siteBase}/lt/legal/terms`).trim()
  const privacyUrl = (c.privacyPolicyWebUrl || `${siteBase}/lt/legal/privacy`).trim()
  return body
    .replace(/\{\{APP_NAME\}\}/g, c.appName)
    .replace(/\{\{SUPPORT_EMAIL\}\}/g, c.supportEmail)
    .replace(/\{\{INFO_EMAIL\}\}/g, c.infoEmail)
    .replace(/\{\{ENTITY\}\}/g, c.legalEntityName)
    .replace(/\{\{COMPANY_CODE\}\}/g, c.companyCode)
    .replace(/\{\{VAT_CODE\}\}/g, c.vatCode)
    .replace(/\{\{ADDRESS\}\}/g, c.registeredAddress)
    .replace(/\{\{LAW\}\}/g, c.governingLawCountry)
    .replace(/\{\{MIN_AGE\}\}/g, String(c.minAgeYears))
    .replace(/\{\{UPDATED\}\}/g, c.legalLastUpdated)
    .replace(/\{\{PRIVACY_URL\}\}/g, privacyUrl)
    .replace(/\{\{TERMS_URL\}\}/g, termsUrl)
}

const EN: Record<LegalDocId, Doc> = {
  privacy: {
    title: 'Privacy Policy',
    body: fill(`Last updated: {{UPDATED}}

This Privacy Policy describes how {{ENTITY}} ("we", "us") processes personal data when you use the mobile application {{APP_NAME}} ("App") and related services.

1. Data controller
The controller of personal data related to the App is {{ENTITY}}. Contact: {{SUPPORT_EMAIL}}.

2. What data we collect
• Account data: email address, password hash (we do not store your password in plain text), display name if you provide it.
• Listings and marketplace activity: titles, descriptions, prices, phone model selections, condition, shipment preferences, photos you upload, IMEI numbers you submit for verification, offer and transaction records connected to your account.
• Technical data: device type, operating system version, app version, approximate timestamps of requests, and diagnostic information needed to operate and secure the service.
• Payments: card payments may be processed by Stripe (or another payment processor we enable). We typically receive payment status and limited transaction metadata—not your full card number—which is handled by the payment provider under their privacy policy.

3. Purposes and legal bases (GDPR)
We process data to perform the contract with you (account, listings, offers, payments where applicable), for our legitimate interests (fraud prevention, service improvement, security, analytics in aggregated form where used), and to comply with legal obligations. Where required, we rely on your consent (for example, optional marketing—if offered separately in the product).

4. IMEI and device checks
When you submit an IMEI, we and/or our subprocessors may query technical databases to help detect invalid or blocked identifiers and to suggest device information. Results are not a guarantee of legal title, authenticity, or that a device is not stolen in every jurisdiction.

5. Photos and automated analysis
If you use optional features that analyse listing photos (for example, cosmetic or pricing hints), images are processed to provide that functionality. Do not upload photos containing unnecessary sensitive personal data of third parties.

6. Sharing and subprocessors
We use infrastructure and software providers (hosting, email, payment, analytics, error reporting) who process data on our behalf under agreements. We may disclose information if required by law or to protect rights, safety, and security.

7. International transfers
If providers process data outside the European Economic Area, we use appropriate safeguards such as Standard Contractual Clauses where required.

8. Retention
We retain data as long as your account is active and as needed to resolve disputes, enforce agreements, and meet legal requirements. Some backups may persist for a limited period after deletion.

9. Your rights
Depending on applicable law, you may have the right to access, rectify, erase, restrict processing, object, and data portability, and to withdraw consent where processing is consent-based. You may lodge a complaint with a supervisory authority. Contact {{SUPPORT_EMAIL}} to exercise rights.

10. Children
The App is not directed at individuals under {{MIN_AGE}}. Do not register if you are younger than {{MIN_AGE}}.

11. Changes
We may update this policy and will indicate the "Last updated" date. Material changes may be communicated in the App or by email where appropriate.

12. Store listings
For convenience, the same policy may be published at: {{PRIVACY_URL}}.`),
  },
  terms: {
    title: 'Terms of Service',
    body: fill(`Last updated: {{UPDATED}}

These Terms of Service ("Terms") govern your use of the {{APP_NAME}} application and related services operated by {{ENTITY}} ("we", "us").

1. The service
{{APP_NAME}} provides an online marketplace-style environment to list and discover used phones, submit price offers, and (where enabled) coordinate payments and shipping status between users. We are not the seller of listed devices unless expressly stated.

2. Eligibility and account
You must be at least {{MIN_AGE}} years old and legally able to enter a contract. You are responsible for account credentials and for activity under your account. Provide accurate information.

3. Listings, offers, and transactions
Sellers are responsible for the accuracy of listings, lawful possession of devices, and honouring accepted deals subject to product rules. Buyers are responsible for reviewing listings and for payment obligations where payment features are enabled. Offers and acceptance create commitments between users as described in the App; we may facilitate messaging of status but peer shipping and handover details are primarily between users unless we offer a specific logistics integration.

4. Prohibited conduct
You must not: list stolen or unlawfully obtained devices; circumvent IMEI or security checks; harass users; upload malware; scrape the service in a way that harms performance; impersonate others; manipulate prices fraudulently; or use the service in violation of law. We may suspend or terminate accounts for violations.

5. IMEI and risk
IMEI and similar checks are tools to reduce risk but are not a warranty of title, authenticity, or absence of claims by third parties. You remain responsible for due diligence.

6. Fees and payments
If we charge fees or use third-party payment processors, the applicable fee structure and processor terms apply. Taxes may be your responsibility as required by law.

7. Intellectual property
The App, branding, and content we provide are protected. You retain rights to content you upload; you grant us a licence to host, display, and process your content to operate the service.

8. Disclaimers
The service is provided on an "as is" and "as available" basis to the maximum extent permitted by law. We do not guarantee uninterrupted or error-free operation.

9. Limitation of liability
To the maximum extent permitted by applicable law, we are not liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data, or goodwill, arising from your use of the service. Our aggregate liability for claims relating to the service in any twelve-month period is limited to the greater of (a) one hundred euros (€100) or (b) fees you paid to us for the service in that period (excluding pass-through payment amounts), except where liability cannot be limited by law.

10. Indemnity
You will defend and indemnify us against claims arising from your listings, your breach of these Terms, or your violation of law, subject to applicable law.

11. Governing law and disputes
These Terms are governed by the laws of {{LAW}}, without regard to conflict-of-law rules, unless mandatory consumer protections in your country provide otherwise. Courts or competent bodies in {{LAW}} have jurisdiction where permitted.

12. Changes
We may modify these Terms. We will update the "Last updated" date and, where required, provide additional notice.

13. Contact
{{SUPPORT_EMAIL}}

Public copy (if published): {{TERMS_URL}}.`),
  },
  buyer_protection: {
    title: 'Buyer protection',
    body: fill(`Last updated: {{UPDATED}}

This page explains how {{APP_NAME}} helps reduce risk for buyers when payment features are enabled. It supplements the Terms of Service and does not replace your statutory consumer rights.

1. What buyer protection covers
Where Stripe Checkout (or another payment flow we enable) is used for a purchase, we aim to hold buyer funds until key steps in the deal are recorded in the App—typically seller shipment and buyer confirmation of delivery—before releasing payout to the seller, subject to product rules and processor terms.

2. How a protected purchase usually works
• You review the listing, IMEI hints, and condition details before paying.
• You pay through the in-app checkout; payment is processed by our payment provider (e.g. Stripe), not sent directly to the seller’s personal account at checkout.
• The seller ships or arranges handover as agreed in chat; status updates appear in your deals area.
• You confirm receipt when the device matches the listing; payout timing follows the timelines shown in the App (including any inspection or guarantee route you chose).

3. Routes and limits
• “Direct” or peer-style contact with the seller may not include the same payment hold—read the purchase options before you pay.
• Optional guarantee or inspection routes may add steps or fees; those are described at checkout.
• IMEI and condition tools reduce risk but are not a warranty of legal title, authenticity, or that a device is free of third-party claims.

4. Problems, disputes, and chargebacks
If the device is materially not as described, damaged in transit, or you suspect fraud, contact the seller in chat first when safe to do so, then email {{SUPPORT_EMAIL}} with your transaction reference and evidence. We may help facilitate status updates or review abuse; we do not act as a court or arbitrator. You may also have rights with your payment provider or under applicable consumer law.

5. Not insurance
Buyer protection is a marketplace process feature, not insurance. {{ENTITY}} is not the seller of listed devices unless expressly stated.

6. Questions
{{SUPPORT_EMAIL}}`),
  },
  eula: {
    title: 'End User License Agreement (EULA)',
    body: fill(`Last updated: {{UPDATED}}

This End User License Agreement ("EULA") supplements the {{APP_NAME}} Terms of Service and applies to the {{APP_NAME}} software.

1. Licensed application
Subject to your compliance with these Terms and this EULA, {{ENTITY}} grants you a personal, non-exclusive, non-transferable, non-sublicensable, revocable licence to install and use the App on devices you own or control.

2. Apple App Store (iOS)
If you obtained the App through the Apple App Store, you acknowledge that:
• Apple is not a party to this EULA and has no obligation to provide support for the App.
• Your use must comply with the Apple Media Services Terms and Conditions, including usage rules.
• In the event of conflict between Apple’s applicable terms and this EULA regarding use of the App on iOS, Apple’s terms apply where required by Apple’s policies.
• Apple’s Standard Licensed Application End User License Agreement may apply to your download from the App Store as described in Apple’s terms at the time of download.

3. Google Play (Android)
If you obtained the App through Google Play, your use must comply with Google Play Terms of Service and Google’s policies.

4. Restrictions
You may not: copy the App except as permitted by law; modify, reverse engineer, decompile, or disassemble the App except where statutory rights cannot be waived; rent, lease, sell, or redistribute the App; remove proprietary notices; or use the App to build a competing product.

5. Updates
We may provide updates that are required for security or functionality. Continued use after update constitutes acceptance unless applicable law requires otherwise.

6. Termination
The licence ends when you delete the App or when we terminate your access in accordance with the Terms. Sections that by nature should survive will survive.

7. Open-source components
Certain components may be subject to open-source licences. See the "Open-source notices" section in the App for summary information.

8. Contact
{{SUPPORT_EMAIL}}`),
  },
  acceptable_use: {
    title: 'Community & acceptable use',
    body: fill(`Last updated: {{UPDATED}}

To keep {{APP_NAME}} safe and trustworthy, you agree to:

1. Lawful trade only
List and purchase devices only where you have the right to sell or buy them. Do not list devices you know or should reasonably suspect are stolen, counterfeit in a deceptive way, or subject to export or sanctions restrictions.

2. Honest listings
Describe condition, defects, and accessories accurately. Use your own photos where required by product rules.

3. Respectful behaviour
Do not threaten, harass, discriminate, or dox others. Do not spam offers or manipulate the marketplace.

4. Security
Do not probe, disrupt, or overload our systems. Do not attempt unauthorised access to data or accounts.

5. Enforcement
We may remove content, hide listings, or suspend or terminate accounts for breaches. We may cooperate with law enforcement when legally required.

6. Reporting
Report concerns to {{SUPPORT_EMAIL}} with relevant details (listing links, user identifiers we display, and screenshots where safe to share).`),
  },
  support: {
    title: 'Support & contact',
    body: fill(`{{APP_NAME}} — help and contact

Primary support email: {{SUPPORT_EMAIL}}

We aim to respond within two (2) business days for general account and technical issues. Complex disputes or legal requests may take longer.

What to include
• Your registered email address
• A short description of the problem
• Screenshots (if relevant) without exposing passwords or payment card numbers

Bug reports
Please describe steps to reproduce, device model, OS version, and app version from your device settings.

Store reviews
We cannot resolve individual account issues through public store reviews alone—email is the reliable channel.

Legal and privacy requests
Mark the subject line clearly (e.g. "GDPR request" or "Privacy question") so we can route your message.

Business / partnerships
Use the same contact; include company details and jurisdiction.

Note: {{ENTITY}} does not provide legal advice about consumer transactions between buyers and sellers.`),
  },
  licenses: {
    title: 'Open-source notices',
    body: fill(`The {{APP_NAME}} application is built with open-source software including (non-exhaustively) React Native and Expo, released under licences such as the MIT Licence.

You can obtain source code and licence texts for those components from their respective repositories (for example, https://github.com/facebook/react-native and https://github.com/expo/expo).

This notice does not modify the licence terms of any third-party component. Where required, copyright notices are retained in upstream distributions.

If you need a complete machine-readable attribution bundle for a specific release, contact {{SUPPORT_EMAIL}} with your app version.`),
  },
  account_data: {
    title: 'Account & data deletion',
    body: fill(`Last updated: {{UPDATED}}

Account deletion
You may request deletion of your {{APP_NAME}} account and associated personal data by emailing {{SUPPORT_EMAIL}} from your registered email address with the subject "Account deletion". We may ask you to verify identity.

What we delete
We will delete or anonymise personal data that we no longer need to retain, subject to legal exceptions (for example, invoice or tax records, fraud-prevention logs where retention is required by law).

Listings and transactions
Some information may remain in anonymised or aggregated form. Information that must be kept to document a completed transaction between users may be retained as required by law and legitimate interests (for example, dispute resolution).

Withdrawal
You may withdraw consent where processing is consent-based, without affecting the lawfulness of processing before withdrawal.

Questions
Contact {{SUPPORT_EMAIL}} for privacy-related questions.

Apple App Store
Apple may require a way for users to initiate account deletion from within the app—this section and the email process above satisfy that expectation when supported by timely processing on our side.`),
  },
}

const LT: Record<LegalDocId, Doc> = {
  privacy: {
    title: 'Privatumo politika',
    body: fill(`Redakcija: {{UPDATED}}

Mes suvokiame ypatingą svarbą, tenkančią Jūsų asmens duomenų apsaugai. {{ENTITY}} (įm. kodas {{COMPANY_CODE}}) valdo prekyvietę {{APP_NAME}}.

1. Duomenų valdytojas
Duomenų valdytojas — {{ENTITY}}. Kontaktai: {{SUPPORT_EMAIL}} · {{INFO_EMAIL}}.

2. Kas tai reiškia?
Naudodamiesi {{APP_NAME}}, Jūs sutinkate su šia Privatumo politika. Jei nesutinkate — neturite teisės naudotis paslauga.

3. Kokius duomenis renkame
• Paskyros duomenys: el. paštas, slaptažodžio maiša (slaptažodžio atviru tekstu nesaugome), vardas / rodomas vardas, jei nurodėte.
• Skelbimai ir veikla turguje: pavadinimai, aprašymai, kainos, telefono modelio pasirinkimai, būklė, siuntimo nuostatos, jūsų įkeliamos nuotraukos, IMEI, pateikti patvirtinimui, pasiūlymų ir sandorių įrašai, susieti su paskyra.
• Techniniai duomenys: įrenginio tipas, OS versija, programėlės versija, užklausų laiko žymos, diagnostinė informacija, reikalinga paslaugai ir saugumui.
• Mokėjimai: kortelių mokėjimus gali apdoroti Stripe (ar kitas įjungtas apdorotojas). Paprastai gauname mokėjimo būseną ir ribotą sandorio metaduomenis — ne pilką kortelės numerį, kurį tvarko mokėjimo tiekėjas pagal savo privatumo politiką.

3. Tikslai ir teisiniai pagrindai (BDAR)
Tvarkome duomenis sutarties vykdymui (paskyra, skelbimai, pasiūlymai, mokėjimai), teisėtais interesais (sukčiavimo prevencija, paslaugos tobulinimas, saugumas, suvestinė analitika, jei naudojama) ir teisės aktų laikymuisi. Kur reikia — remiamės sutikimu (pvz. atskirai siūloma rinkodara).

4. IMEI ir įrenginių tikrinimas
Pateikus IMEI, mes ir/ar subprocesoriai galime kreiptis į technines duomenų bazes, siekiant aptikti netinkamus ar blokuotus identifikatorius ir pasiūlyti įrenginio informaciją. Rezultatai nėra teisinės nuosavybės, autentiškumo ar visų jurisdikcijų „nevogto“ įrenginio garantija.

5. Nuotraukos ir automatizuota analizė
Jei naudojate papildomas funkcijas, analizuojančias skelbimo nuotraukas (pvz. kosmetines / kainų gaires), nuotraukos tvarkomos tam tikslui. Nekelkite nuotraukų su nereikalingais trečiųjų šalių asmens duomenimis.

6. Perdavimas ir subprocesoriai
Naudojame infrastruktūros ir programinės įrangos tiekėjus (hostingą, el. paštą, mokėjimus, analitiką, klaidų raportavimą), kurie tvarko duomenis mūsų vardu pagal sutartis. Galime atskleisti informaciją, jei to reikalauja įstatymai ar siekiame apsaugoti teises, saugumą.

7. Tarptautiniai perdavimai
Jei tiekėjai tvarko duomenis už EEA ribų, taikome tinkamas apsaugas, pvz. standartines sutartines sąlygas, kai reikia.

8. Saugojimas
Saugome duomenis, kol paskyra aktyvi ir kol reikia ginčams, sutartims vykdyti ir teisės aktams. Atsarginės kopijos gali likti ribotą laiką po ištrynimo.

9. Jūsų teisės
Pagal taikomą teisę galite turėti teisę susipažinti, taisyti, ištrinti, apriboti tvarkymą, nesutikti, duomenų perkeliamumą ir atšaukti sutikimą, kai tvarkymas grindžiamas sutikimu. Skundą galite pateikti priežiūros institucijai. Prašymus siųskite į {{SUPPORT_EMAIL}}.

10. Vaikai
Programėlė neskirta asmenims jaunesniems nei {{MIN_AGE}} metų. Neregistruokitės, jei esate jaunesni.

11. Pakeitimus
Politiką galime atnaujinti; nurodysime „Paskutinis atnaujinimas“. Apie esminius pakeitimus galime pranešti Programėlėje ar el. paštu.

12. Parduotuvių formos
Patogumui ta pati politika gali būti paskelbta adresu: {{PRIVACY_URL}}.`),
  },
  terms: {
    title: 'Paslaugų teikimo sąlygos',
    body: fill(`Redakcija: {{UPDATED}}

Mes esame {{ENTITY}} (įm. kodas {{COMPANY_CODE}}). Kontaktai: {{SUPPORT_EMAIL}}.

1. Paslauga
{{APP_NAME}} teikia tarpininkavimo (prieglobos) paslaugas — skelbimų talpinimą, pasiūlymus ir susijusias funkcijas naudotų telefonų prekybai.

2. Tinkamumas ir paskyra
Jums turi būti bent {{MIN_AGE}} metų ir teisėta galimybė sudaryti sutartį. Esate atsakingi už prisijungimo duomenis ir veiksmus paskyroje. Pateikite tikslius duomenis.

3. Skelbimai, pasiūlymai ir sandoriai
Pardavėjai atsakingi už skelbimų tikslumą, teisėtą įrenginių valdymą ir priimtų sandorių vykdymą pagal produkto taisykles. Pirkėjai atsakingi už skelbimų peržiūrą ir mokėjimo įsipareigojimus, kai mokėjimo funkcijos įjungtos. Pasiūlymai ir priėmimas sukuria įsipareigojimus tarp naudotojų, kaip nurodyta Programėlėje; mes galime padėti fiksuoti būsenas, tačiau siuntimas „tarpusavyje“ dažniausiai derinamas tarp naudotojų, nebent siūloma logistikos integracija.

4. Draudžiama veikla
Draudžiama: skelbti vogtus ar neteisėtai įgytus įrenginius; apeiti IMEI ar saugumo tikrinimus; priekabiauti; kelti kenkėjišką programinę įrangą; rinkti duomenis, kenkiančiai paveikti paslaugą; apsimesti kitais; manipuliuoti kainomis sukčiavimo tikslais; naudotis paslauga, pažeidžiant įstatymus. Už pažeidimus galime sustabdyti ar nutraukti paskyras.

5. IMEI ir rizika
IMEI ir panašūs tikrinimai mažina riziką, bet negarantuoja nuosavybės, autentiškumo ar trečiųjų šalių pretenzijų nebuvimo. Jūsų pareiga — derama patikra.

6. Mokesčiai ir mokėjimai
Jei taikomi mokesčiai ar naudojami trečiųjų šalių apdorotojai, galioja atitinkama kainodara ir apdorotojų sąlygos. Mokesčius pagal įstatymus galite turėti mokėti jūs.

7. Intelektinė nuosavybė
Programėlė, prekės ženklas ir mūsų teikiamas turinys yra saugomi. Turinį, kurį įkeliate, išsaugote savo teises; suteikiate mums licenciją jį hostinti, rodyti ir tvarkyti paslaugai vykdyti.

8. Atsakomybės apribojimai
Paslauga teikiama „tokia, kokia yra“ ir „kai prieinama“ taikomų įstatymų ribose. Negarantuojame nepertraukiamo ar be klaidų veikimo.

9. Žalos atlyginimo riba
Taikomų įstatymų ribose neatsakome už netiesioginę, atsitiktinę, ypatingą, pasekminę ar baudinę žalą ar pelno, duomenų ar reputacijos praradimą. Bendra mūsų atsakomybė už pretenzijas dėl paslaugos bet kuriuo dvylikos mėnesių laikotarpiu apribota didesne suma iš: (a) šimto eurų (100 €) arba (b) mokesčių, kuriuos mums sumokėjote už paslaugą tame laikotarpyje (be perduotų mokėjimo sumų), išskyrus atvejus, kai riboti draudžiama įstatymu.

10. Atlyginimas
Jūs ginate ir atlyginate mums žalas, kylančias dėl jūsų skelbimų, Sąlygų pažeidimo ar įstatymų pažeidimo, laikantis įstatymų.

11. Taikoma teisė ir ginčai
Sąlygoms taikoma {{LAW}} teisė, nebent privaloma vartotojų apsauga jūsų valstybėje numato kitaip. Teismai ar kompetentingi organai {{LAW}}, kur leidžiama.

12. Pakeitimai
Galime keisti Sąlygas; atnaujinsime datą ir, kur reikia, pateiksime papildomą pranešimą.

13. Kontaktai
{{SUPPORT_EMAIL}}

Pilna versija: {{TERMS_URL}}.`),
  },
  buyer_protection: {
    title: 'Pirkėjo apsauga',
    body: fill(`Redakcija: {{UPDATED}}

Šiame skyriuje paaiškiname, kaip {{APP_NAME}} padeda sumažinti pirkėjo riziką, kai įjungtos mokėjimo funkcijos. Tai papildo paslaugų teikimo sąlygas ir nepakeičia jūsų įstatymų numatytų vartotojų teisių.

1. Ką apima pirkėjo apsauga
Kai pirkimui naudojamas Stripe Checkout (ar kitas mūsų įjungtas mokėjimo srautas), siekiame laikyti pirkėjo lėšas, kol Programėlėje užfiksuojami svarbiausi sandorio žingsniai — paprastai pardavėjo siuntimas ir pirkėjo pristatymo patvirtinimas — prieš perduodant išmoką pardavėjui, laikantis produkto taisyklių ir mokėjimo tiekėjo sąlygų.

2. Kaip paprastai vyksta apsaugotas pirkimas
• Prieš mokėdami peržiūrite skelbimą, IMEI gaires ir būklės aprašymą.
• Mokate per programėlės atsiskaitymą; mokėjimą apdoroja mūsų mokėjimo tiekėjas (pvz. Stripe), o ne tiesiogiai pardavėjo asmeninė sąskaita.
• Pardavėjas siunčia arba susitinka perduoti, kaip sutarta pokalbyje; būsenas matote skiltyje „Sandoriai“.
• Patvirtinate gavimą, kai įrenginys atitinka skelbimą; išmokos laikas — pagal Programėlėje rodomus terminus (įskaitant pasirinktą garantinį ar patikros kelią).

3. Keliai ir ribos
• „Tiesioginis“ ar peer stiliaus kontaktas su pardavėju gali neapimti to paties mokėjimo sulaikymo — prieš mokėdami perskaitykite pirkimo variantus.
• Pasirenkami garantiniai ar patikros keliai gali pridėti žingsnių ar mokesčių; tai aprašyta atsiskaityme.
• IMEI ir būklės įrankiai mažina riziką, bet negarantuoja teisėtos nuosavybės, autentiškumo ar trečiųjų šalių pretenzijų nebuvimo.

4. Problemos, ginčai ir chargeback
Jei įrenginys esmingai neatitinka skelbimo, sugadintas pervežimo metu ar įtariate sukčiavimą, saugiai pirmiausia susisiekite su pardavėju pokalbyje, tada rašykite {{SUPPORT_EMAIL}} nurodydami sandorio nuorodą ir įrodymus. Galime padėti atnaujinti būsenas ar peržiūrėti pažeidimus; neveikiame kaip teismas ar arbitras. Taip pat galite turėti teisių pas mokėjimo tiekėją ar pagal taikomą vartotojų teisę.

5. Tai nėra draudimas
Pirkėjo apsauga — prekyvietės proceso funkcija, ne draudimo polisas. {{ENTITY}} nėra skelbimuose parduodamų įrenginių pardavėjas, nebent aiškiai nurodyta kitaip.

6. Klausimai
{{SUPPORT_EMAIL}}`),
  },
  eula: {
    title: 'Galutinio naudotojo licencinė sutartis (EULA)',
    body: fill(`Paskutinis atnaujinimas: {{UPDATED}}

Ši EULA papildo {{APP_NAME}} paslaugų sąlygas ir taikoma {{APP_NAME}} programinei įrangai.

1. Licencijuota programėlė
Laikydamiesi Sąlygų ir šios EULA, {{ENTITY}} suteikia jums asmeninę, ne išimtinę, neperleidžiamą, ne sublicencijuojamą, atšaukiamą licenciją diegti ir naudoti Programėlę įrenginiuose, kuriuos valdote.

2. Apple App Store (iOS)
Jei Programėlę įsigijote per Apple App Store, pripažįstate:
• Apple nėra šios EULA šalis ir neprivalo teikti Programėlės palaikymo;
• naudojimasis turi atitikti Apple Media paslaugų sąlymas ir naudojimo taisykles;
• konfliktų atveju dėl naudojimo iOS įrenginiuose taikomos Apple politikos, kai to reikalauja Apple;
• Apple standartinė licencijuotos programėlės galutinio naudotojo licencinė sutartis gali taikytis pagal Apple taisykles atsisiuntimo metu.

3. Google Play (Android)
Jei įsigijote per Google Play, naudojimasis turi atitikti Google Play paslaugų sąlygas ir Google politikas.

4. Apribojimai
Draudžiama: kopijuoti Programėlę, išskyrus įstatymų leidžiamus atvejus; modifikuoti, dekompiliuoti ar išardyti, išskyrus ten, kur teisės neatsisakomos; nuomoti, parduoti ar platinti Programėlę; šalinti nuosavybės ženklus; naudoti kurti konkuruojančiam produktui.

5. Atnaujinimai
Galime teikti saugumo ar funkcionalumo atnaujinimus. Tęsdami naudojimą po atnaujinimo sutinkate, nebent įstatymai numato kitaip.

6. Nutraukimas
Licencija baigiasi ištrinus Programėlę ar nutraukus prieigą pagal Sąlygas. Turi išlikti nuostatos, kurioms būdinga galiojimas ir po nutraukimo.

7. Atvirojo kodo komponentai
Kai kurie komponentai gali būti platinami pagal atvirojo kodo licencijas — žr. „Atvirojo kodo pranešimai“ Programėlėje.

8. Kontaktai
{{SUPPORT_EMAIL}}`),
  },
  acceptable_use: {
    title: 'Bendruomenės ir tinkamo naudojimo taisyklės',
    body: fill(`Paskutinis atnaujinimas: {{UPDATED}}

Kad {{APP_NAME}} būtų saugi ir patikima:

1. Tik teisėta prekyba
Skelbkite ir pirkite tik ten, kur turite teisę parduoti ar pirkti. Neskelbkite įrenginių, kuriuos žinote ar turėtumėte pagrįstai įtarti esant vogtais, klaidinančiai padirbtais ar eksporto / sankcijų apribojimams.

2. Sąžiningi skelbimai
Tiksliai aprašykite būklę, defektus ir priedus. Kur reikalaujama taisyklių — naudokite savo nuotraukas.

3. Pagarbus elgesys
Nekelkite grėsmių, nepriekabiaukite, nediskriminuokite ir neatskleiskite privatumo. Nesiųskite perteklinių pasiūlymų ir nemanipuliuokite turgumi.

4. Saugumas
Neprobinkite, negadinkite ir neapkraunkite sistemų. Nesiekite neteisėtos prieigos prie duomenų ar paskyrų.

5. Vykdymas
Pažeidus taisykles galime šalinti turinį, slėpti skelbimus ar stabdyti / naikinti paskyras. Bendradarbiausime su teisėsauga, kai įstatymai reikalauja.

6. Pranešimai
Praneškite į {{SUPPORT_EMAIL}} su detalėmis (skelbimo nuorodos, rodomi naudotojo identifikatoriai, ekrano kopijos, jei saugu).`),
  },
  support: {
    title: 'Palaikymas ir kontaktai',
    body: fill(`{{APP_NAME}} — pagalba ir ryšys

Pagrindinis palaikymo el. paštas: {{SUPPORT_EMAIL}}

Siekiame atsakyti per dvi (2) darbo dienas dėl bendrų paskyros ir techninių klausimų. Sudėtingesni ginčai ar teisiniai prašymai gali užtrukti ilgiau.

Ką įtraukti
• Registruotas el. paštas
• Trumpas problemos aprašymas
• Ekrano kopijos (jei tinka) be slaptažodžių ir kortelės numerių

Klaidų pranešimai
Aprašykite žingsnius, įrenginį, OS ir programėlės versiją.

Parduotuvių atsiliepimai
Atskirų paskyrų problemų per viešus atsiliepimus neišspręsime — patikimas kanalas yra el. paštas.

Teisiniai ir privatumo prašymai
Temoje aiškiai nurodykite (pvz. „BDAR prašymas“ arba „Privatumo klausimas“).

Verslas / partnerystės
Tas pats kontaktas; nurodykite įmonę ir jurisdikciją.

Pastaba: {{ENTITY}} neteikia teisinės konsultacijos dėl pirkėjo ir pardavėjo sandorių.`),
  },
  licenses: {
    title: 'Atvirojo kodo pranešimai',
    body: fill(`Programėlė {{APP_NAME}} sukurta naudojant atvirojo kodo programinę įrangą, įskaitant (neišsamiai) React Native ir Expo, platinamus pagal licencijas, tokias kaip MIT.

Išeities tekstus ir licencijas rasite atitinkamuose saugyklose (pvz. https://github.com/facebook/react-native ir https://github.com/expo/expo).

Šis pranešimas nekeičia trečiųjų šalių licencijų sąlygų.

Jei reikia pilnos atributikos paketo konkrečiai versijai, rašykite į {{SUPPORT_EMAIL}} nurodydami programėlės versiją.`),
  },
  account_data: {
    title: 'Paskyra ir duomenų ištrynimas',
    body: fill(`Paskutinis atnaujinimas: {{UPDATED}}

Paskyros ištrynimas
Galite prašyti ištrinti {{APP_NAME}} paskyrą ir susijusius asmens duomenis, rašydami į {{SUPPORT_EMAIL}} iš registruoto el. pašto su tema „Account deletion“ / „Paskyros ištrynimas“. Galime prašyti tapatybės patvirtinimo.

Ką ištriname
Ištrinsime ar nuasmeninsime asmens duomenis, kurių nebereikia, išskyrus įstatymų išimtis (pvz. sąskaitų / mokesčių apskaita, sukčiavimo prevencijos žurnalai, jei saugojimą reikalauja įstatymas).

Skelbimai ir sandoriai
Kai kuri informacija gali likti suvestinėje ar nuasmeninta forma. Informacija, kurią privalome saugoti dėl įvykdyto sandorio tarp naudotojų, gali būti saugoma pagal įstatymus ir teisėtus interesus (pvz. ginčų sprendimui).

Atsisakymas
Galite atšaukti sutikimą, kai tvarkymas grindžiamas sutikimu, nepažeidžiant ankstesnio tvarkymo teisėtumo.

Klausimai
{{SUPPORT_EMAIL}}

Apple App Store
Apple gali reikalauti būdo inicijuoti paskyros ištrynimą iš programėlės — šis skyrius ir el. pašto procesas atitinka šį lūkestį, jei užklausos vykdomos laiku.`),
  },
}

export function getLegalDoc(id: LegalDocId, locale: LegalLocale): Doc {
  const pack = locale === 'lt' ? LT : EN
  const doc = pack[id]
  return { title: doc.title, body: fill(doc.body) }
}

export const LEGAL_DOC_ORDER: LegalDocId[] = [
  'terms',
  'buyer_protection',
  'privacy',
  'eula',
  'acceptable_use',
  'account_data',
  'support',
  'licenses',
]
